// ============================================================
// ConfirmDialogue.tsx
// Reusable confirmation dialogue — used before destructive or significant
// actions (delete, unlink, sign out, finalise, reopen…).
// Props: isOpen, title, message, confirmLabel, cancelLabel?, onConfirm, onCancel,
// children? (an extra field / figures under the message), confirmDisabled?
// (+ optional `tone`). API unchanged — every existing call site works.
//
// LIGHT "Option B" look (approved mockup casevo-confirm-dialog-options.html,
// .B :59-63 + shared .dlg/.ttl/.msg/.btn/.ic :41-53): a compact 420px surface
// card — NO green hero band — with a small tinted icon circle beside the
// title (red warning when destructive, mint/green otherwise), the message,
// and right-aligned Cancel + confirm.
//
// Behaviour comes from the shared ModalFrame in BARE mode (no hero/body/
// footer chrome): the overlay, Esc (top-most frame only), backdrop
// press+release, focus trap + restore, and z-[95] stacking above other
// modals (ModalFrame z-[70], the link picker z-[80]). Focus starts on
// Cancel so Enter/Space never fires the destructive action by accident.
//
// Confirm colour: destructive actions (delete / remove / unlink / deactivate
// / revoke / disconnect / reset / discard / erase) use the DANGER fill;
// everything else (finalise, reopen, update, sign out) the green primary.
// Inferred from label/title so call sites don't change; `tone` overrides.
// ============================================================
import React, { useRef } from 'react'
import ModalFrame from './ModalFrame'

interface ConfirmDialogueProps {
  isOpen: boolean
  title: string
  message: string
  confirmLabel?: string
  // Optional cancel-button text (default "Cancel").
  cancelLabel?: string
  onConfirm: () => void
  onCancel: () => void
  // Optional override of the inferred confirm colour.
  tone?: 'danger' | 'primary'
  // Optional content under the message (e.g. a date field, a before/after
  // figure) and a way to hold the confirm button until it's valid / busy.
  children?: React.ReactNode
  confirmDisabled?: boolean
}

const DESTRUCTIVE = /\b(delete|remove|unlink|deactivate|revoke|disconnect|reset|discard|erase)\b/i
const SIGN_OUT = /\bsign\s?out\b|\blog\s?out\b/i

// Mockup .btn (:46-49) — 13.5px / 8px radius / 8×16 padding.
export const DIALOG_BTN_CANCEL =
  'inline-flex items-center justify-center whitespace-nowrap text-[13.5px] font-medium rounded-[8px] px-[16px] py-[8px] ' +
  'border border-casevo-border bg-casevo-surface text-casevo-mid hover:border-casevo-mid hover:text-casevo-forest ' +
  'transition-colors focus:outline-none casevo-focus disabled:opacity-50'
// Danger fill — #dc2626 / dark #fca5a5 with white / dark ink (mockup --danger/--dangerInk).
export const DIALOG_BTN_DANGER =
  'inline-flex items-center justify-center whitespace-nowrap text-[13.5px] font-semibold rounded-[8px] px-[16px] py-[8px] ' +
  'border border-transparent bg-casevo-danger text-white dark:text-[#2a0f0b] hover:bg-[#b91c1c] dark:hover:bg-[#f87171] ' +
  'transition-colors focus:outline-none casevo-focus disabled:opacity-50 disabled:cursor-not-allowed'
// Green primary — casevo-primary (#2d6a4f both themes) with white text.
export const DIALOG_BTN_PRIMARY =
  'inline-flex items-center justify-center whitespace-nowrap text-[13.5px] font-semibold rounded-[8px] px-[16px] py-[8px] ' +
  'border border-transparent bg-casevo-primary text-white hover:bg-casevo-primary-hover ' +
  'transition-colors focus:outline-none casevo-focus disabled:opacity-50 disabled:cursor-not-allowed'

// Mockup .dlg + .B — 420px, 14px radius, 1px border, popover shadow
// (= MyWorkParts.tsx:31 / NotificationsPanel.tsx:211), 20/22/18 padding.
const CARD =
  'max-w-[420px] rounded-[14px] border border-casevo-border bg-casevo-surface text-casevo-forest pt-[20px] px-[22px] pb-[18px] ' +
  'shadow-[0_18px_44px_rgba(10,25,17,0.20),0_2px_6px_rgba(10,25,17,0.08)] dark:shadow-[0_18px_44px_rgba(0,0,0,0.5),0_2px_6px_rgba(0,0,0,0.3)]'
// Mockup .ic.red / .ic.green (:52-53) — 38px circle, 18px line icon.
const IC_RED = 'bg-[#fbe9e6] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]'
const IC_GREEN = 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]'

const PATH = {
  warn: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  out: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>,
  check: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>,
}

const ConfirmDialogue: React.FC<ConfirmDialogueProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  tone,
  children,
  confirmDisabled,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const danger = tone ? tone === 'danger' : DESTRUCTIVE.test(`${confirmLabel} ${title}`)
  // Icon: red warning when destructive; else sign-out glyph for sign-out
  // confirms (mockup), a check-circle for every other non-destructive one.
  const icon = danger ? PATH.warn : SIGN_OUT.test(`${confirmLabel} ${title}`) ? PATH.out : PATH.check
  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={onCancel}
      bare
      zIndexClassName="z-[95]"
      title={title} // not rendered in bare mode; ariaLabel names the dialog
      ariaLabel={title}
      className={CARD}
      // Focus the SAFE choice first (Enter/Space won't fire the destructive one).
      initialFocusRef={cancelRef}
    >
      <div className="flex gap-[14px]">
        <span aria-hidden="true" className={`w-[38px] h-[38px] rounded-full grid place-items-center shrink-0 ${danger ? IC_RED : IC_GREEN}`}>
          <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">{icon}</svg>
        </span>
        <div className="min-w-0">
          <h3 className="text-[16px] font-semibold tracking-[-0.005em] text-casevo-forest mt-[2px] mb-[5px] leading-[normal]">{title}</h3>
          {/* text-casevo-text-soft kept from the contrast fix (4.8:1 light / 5.0:1 dark). */}
          <p className={`text-[13.5px] leading-[1.55] text-casevo-text-soft mt-0 ${children ? 'mb-[12px]' : 'mb-[18px]'}`}>{message}</p>
          {children && <div className="mb-[18px]">{children}</div>}
        </div>
      </div>
      <div className="flex gap-[8px] justify-end">
        <button ref={cancelRef} type="button" onClick={onCancel} className={DIALOG_BTN_CANCEL}>{cancelLabel}</button>
        <button type="button" onClick={onConfirm} disabled={confirmDisabled} className={danger ? DIALOG_BTN_DANGER : DIALOG_BTN_PRIMARY}>
          {confirmLabel}
        </button>
      </div>
    </ModalFrame>
  )
}
export default ConfirmDialogue
